import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { API_ENDPOINTS } from '../lib/api-endpoints';

interface APITestPageProps {
  onClose: () => void;
}

export default function APITestPage({ onClose }: APITestPageProps) {
  const [baseUrl, setBaseUrl] = useState('http://localhost:5001');
  const [results, setResults] = useState<any>({});
  const [loading, setLoading] = useState<string | null>(null);

  const testAPI = async (name: string, endpoint: string, method: string = 'GET', data?: any) => {
    setLoading(name);
    try {
      const response = await fetch(`${baseUrl}/api${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: data ? JSON.stringify(data) : undefined,
      });
      const result = await response.json();
      setResults((prev: any) => ({ ...prev, [name]: { success: response.ok, data: result, status: response.status } }));
      toast.success(`${name}: ${response.ok ? 'Success' : 'Failed'}`);
    } catch (error: any) {
      setResults((prev: any) => ({ ...prev, [name]: { success: false, error: error.message } }));
      toast.error(`${name}: ${error.message}`);
    } finally {
      setLoading(null);
    }
  };

  const apiTests = [
    { name: 'Auth - Login', endpoint: API_ENDPOINTS.AUTH.LOGIN, method: 'POST', data: { phone: '9999999999', password: 'test123' } },
    { name: 'Auth - Me', endpoint: API_ENDPOINTS.AUTH.ME, method: 'GET' },
    { name: 'Auth - Send OTP', endpoint: API_ENDPOINTS.AUTH.SEND_OTP, method: 'POST', data: { phone: '9999999999' } },
    { name: 'Profile - My Profile', endpoint: API_ENDPOINTS.PROFILE.GET_MY_PROFILE, method: 'GET' },
    { name: 'Profile - Matches', endpoint: API_ENDPOINTS.PROFILE.GET_MATCHES, method: 'GET' },
    { name: 'Chat - Get Chats', endpoint: API_ENDPOINTS.CHAT.GET_CHATS, method: 'GET' },
    { name: 'Interaction - Interests', endpoint: API_ENDPOINTS.INTERACTION.GET_INTERESTS, method: 'GET' },
    { name: 'Plans - Get All', endpoint: API_ENDPOINTS.PLAN.GET_ALL, method: 'GET' },
    { name: 'Plans - My Plan', endpoint: API_ENDPOINTS.PLAN.GET_MY_PLAN, method: 'GET' },
    { name: 'Premium Plans - Get All', endpoint: API_ENDPOINTS.PREMIUM_PLAN.GET_ALL, method: 'GET' },
    { name: 'Notification - Get All', endpoint: API_ENDPOINTS.NOTIFICATION.GET_ALL, method: 'GET' },
    { name: 'Notification - Unread Count', endpoint: API_ENDPOINTS.NOTIFICATION.GET_UNREAD_COUNT, method: 'GET' },
    { name: 'Activity - Get All', endpoint: API_ENDPOINTS.ACTIVITY.GET_ALL, method: 'GET' },
    { name: 'Blog - Get All', endpoint: API_ENDPOINTS.BLOG.GET_ALL, method: 'GET' },
    { name: 'Community - Get Posts', endpoint: API_ENDPOINTS.COMMUNITY.GET_POSTS, method: 'GET' },
    { name: 'Settings - Get All', endpoint: API_ENDPOINTS.SETTINGS.GET_ALL, method: 'GET' },
    { name: 'FAQ - Get All', endpoint: API_ENDPOINTS.FAQ.GET_ALL, method: 'GET' },
    { name: 'Referral - My Code', endpoint: API_ENDPOINTS.REFERRAL.GET_MY_CODE, method: 'GET' },
    { name: 'Wallet - Balance', endpoint: API_ENDPOINTS.WALLET.GET_BALANCE, method: 'GET' },
    { name: 'Marketing - Stats', endpoint: API_ENDPOINTS.MARKETING.GET_STATS, method: 'GET' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1>API Test Page</h1>
          <Button onClick={onClose}>Back to App</Button>
        </div>

        <Card className="p-6 mb-6">
          <div className="flex gap-4 items-center">
            <Input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="Backend URL"
              className="flex-1"
            />
            <Button onClick={() => toast.success('URL Updated')} variant="outline">Update URL</Button>
          </div>
          <p className="text-sm text-gray-500 mt-2">Current: {baseUrl}</p>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {apiTests.map((test) => (
            <Card key={test.name} className="p-4">
              <h3 className="mb-2">{test.name}</h3>
              <p className="text-xs text-gray-500 mb-3">{test.method} {test.endpoint}</p>
              <Button
                onClick={() => testAPI(test.name, test.endpoint, test.method, test.data)}
                disabled={loading === test.name}
                className="w-full mb-2"
              >
                {loading === test.name ? 'Testing...' : 'Test'}
              </Button>
              {results[test.name] && (
                <div className={`text-xs p-2 rounded mt-2 ${results[test.name].success ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className="mb-1">
                    <strong>Status:</strong> {results[test.name].status || 'Error'}
                  </p>
                  <pre className="max-h-32 overflow-auto">
                    {JSON.stringify(results[test.name].data || results[test.name].error, null, 2)}
                  </pre>
                </div>
              )}
            </Card>
          ))}
        </div>

        <Card className="p-6 mt-6">
          <h2 className="mb-4">Admin APIs (Requires Admin Token)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button onClick={() => testAPI('Admin - Dashboard Stats', API_ENDPOINTS.ADMIN.DASHBOARD_STATS, 'GET')}>
              Dashboard Stats
            </Button>
            <Button onClick={() => testAPI('Admin - Get Users', API_ENDPOINTS.ADMIN.GET_USERS, 'GET')}>
              Get Users
            </Button>
            <Button onClick={() => testAPI('Admin - Get Plans', API_ENDPOINTS.ADMIN.GET_PLANS, 'GET')}>
              Get Plans
            </Button>
            <Button onClick={() => testAPI('Admin - Get Coupons', API_ENDPOINTS.ADMIN.GET_COUPONS, 'GET')}>
              Get Coupons
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}